import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, SearchUtil, Time} from 'yougile-util'
import {DataObjectId, PermissionRoleKey, User} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {showConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {noPropagate} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {UserAccess} from '@extensions/user-access'
import {PendingRegistration} from '../user-fields'

const registrationsTablePage = 10

interface State {
  pendingRegistrationsIds: DataObjectId[]
  pendingRegistrations: PendingRegistration[]

  newRegistrationsPending: boolean

  selectedRegistrations: Set<DataObjectId>
  newRegistrationsSince: number

  isInvitePopupOpened: boolean
  isRegistrationsPopupOpened: boolean
  tableMaxShow: number
}

const getInitialState = (): State => {
  return {
    pendingRegistrationsIds: [],
    pendingRegistrations: [],

    newRegistrationsPending: false,

    selectedRegistrations: new Set(),
    newRegistrationsSince: 0,

    isInvitePopupOpened: false,
    isRegistrationsPopupOpened: false,
    tableMaxShow: registrationsTablePage
  }
}

export class PendingCompaniesAdminStore {
  state: State = getInitialState()
  onCompanyChangeId?: number

  pendingRegistrationsCheckHandle: ReturnType<typeof setInterval> | undefined

  constructor() {
    makeAutoObservable(
      this,
      {
        state: observable.shallow,
        pendingRegistrationsCheckHandle: observable.ref
      },
      {autoBind: true}
    )
  }

  isDisabled(): boolean {
    return (
      globals.standalone ||
      !PermissionsChecker.isAdmin() ||
      !Backend.features.pendingRegistrations
    )
  }

  subscribe(): void {
    if (this.isDisabled()) {
      return
    }

    this.onCompanyChangeId = globals.onCompanyChange(this.resetState)

    this.pendingRegistrationsCheckHandle = setInterval(() => {
      void this.updatePendingRegistrations()
    }, Time.minute * 3)
  }

  async updatePendingRegistrations(): Promise<void> {
    if (this.isDisabled()) {
      return
    }

    try {
      const res = await Backend.postJSON<{
        data: {
          userId: DataObjectId
          realName?: string
        }[]
      }>('/data/company-link-register-pending', {
        ...Backend.loadCred(),
        since: '1',
        companyId: globals.getCompanyId()
      })

      const box = Sync.getCurrentSandbox()

      runInAction(() => {
        this.state.pendingRegistrationsIds = res.data.map(x => x.userId)
        this.state.pendingRegistrations = res.data.map(({userId, realName}) => {
          const user = box.get(userId) as User

          return {
            userId,
            timestamp: user?.timestamp ?? 0,
            email: user?.email ?? '',
            username: realName || ''
          }
        })

        this.state.pendingRegistrations.sort(
          (a, b) => b.timestamp - a.timestamp
        )
      })
    } catch (e) {
      logger.log(e)
    }

    this.openRegistrationPopupIfRequired()
  }

  private openRegistrationPopupIfRequired(): void {
    const since = this.getNewRegistrationsSince()

    this.state.newRegistrationsSince = since

    const hasNewPendingRegistrations = this.state.pendingRegistrations.some(
      x => x.timestamp > since
    )

    this.setRegistrationPopupOpen(hasNewPendingRegistrations)
  }

  updateNewRegistrationsSince(): void {
    const box = Sync.getProfile()
    const hub = box.getHub(box.companyId)
    const companyId = globals.getCompanyId()

    if (!hub) {
      return
    }

    hub.data.companyLinkSettings ??= {}
    hub.data.companyLinkSettings[companyId] ??= {}
    hub.data.companyLinkSettings[companyId].newRegistrationsSince = Date.now()
    hub.commit()
  }

  getNewRegistrationsSince(): number {
    const box = Sync.getProfile()
    const hub = box.getHub(box.companyId)
    const companyId = globals.getCompanyId()

    if (!hub) {
      return 0
    }
    const sett = hub.data.companyLinkSettings
    if (!sett?.[companyId]) {
      return 0
    }

    return sett[companyId].newRegistrationsSince ?? 0
  }

  get pendingRegistrationsSince(): PendingRegistration[] {
    return this.state.pendingRegistrations.filter(
      x => x.timestamp > this.state.newRegistrationsSince
    )
  }

  //

  getTablePendingRegistrations(filterQuery: string): PendingRegistration[] {
    const query = filterQuery.trim()

    if (!query) {
      return this.state.pendingRegistrations
    }

    const words = SearchUtil.prepareWords(SearchUtil.cleanStr(query))
    return this.state.pendingRegistrations.filter(
      item =>
        SearchUtil.hasAllWords(item.username ?? '', words) ||
        SearchUtil.hasAllWords(item.email ?? '', words)
    )
  }

  tableShowMore(e: React.MouseEvent): void {
    noPropagate(e)
    this.state.tableMaxShow += registrationsTablePage
  }

  setProjectsForRegistration(
    userId: DataObjectId,
    projectIds: DataObjectId[]
  ): void {
    this.state.pendingRegistrations = this.state.pendingRegistrations.map(x =>
      x.userId === userId ? {...x, projectIds} : x
    )
  }

  setRoleForRegistration(userId: DataObjectId, role: PermissionRoleKey): void {
    this.state.pendingRegistrations = this.state.pendingRegistrations.map(x =>
      x.userId === userId ? {...x, role} : x
    )
  }

  toggleSelect(userId: DataObjectId): void {
    const set = new Set(this.state.selectedRegistrations)
    if (this.state.selectedRegistrations.has(userId)) {
      set.delete(userId)
    } else {
      set.add(userId)
    }
    this.state.selectedRegistrations = set
  }

  toggleSelectAll(): void {
    const selected = this.allSelected
      ? []
      : this.pendingRegistrationsSince.map(x => x.userId)
    this.state.selectedRegistrations = new Set(selected)
  }

  get allSelected(): boolean | undefined {
    const all =
      this.state.selectedRegistrations.size ===
      this.pendingRegistrationsSince.length
    if (all) {
      return true
    }
    const none = !this.state.selectedRegistrations.size
    if (none) {
      return false
    }
    return undefined
  }

  setRegistrationPopupOpen(open: boolean): void {
    if (open !== this.state.isRegistrationsPopupOpened) {
      this.state.isRegistrationsPopupOpened = open
      if (!open) {
        this.updateNewRegistrationsSince()
      }
    }
  }

  async acceptSelectedRegistrations(): Promise<void> {
    const invites = this.state.pendingRegistrations
      .filter(x => this.state.selectedRegistrations.has(x.userId))
      .map(x => ({
        userId: x.userId,
        projectIds: x.projectIds ?? [],
        roleId: x.role ?? 'worker'
      }))
    if (await this.acceptRegistrations(invites)) {
      this.state.selectedRegistrations = new Set()
      if (!this.pendingRegistrationsSince.length) {
        this.setRegistrationPopupOpen(false)
      }
    }
  }

  acceptRegistration(
    userId: DataObjectId,
    projectIds: DataObjectId[] = [],
    roleId: PermissionRoleKey = 'worker'
  ): Promise<boolean> {
    return this.acceptRegistrations([{userId, projectIds, roleId}])
  }

  private async acceptRegistrations(
    invites: {
      userId: DataObjectId
      projectIds: DataObjectId[]
      roleId: PermissionRoleKey
    }[]
  ): Promise<boolean> {
    const set = new Set(invites.map(x => x.userId))
    this.state.pendingRegistrations = this.state.pendingRegistrations.map(x =>
      set.has(x.userId) ? {...x, inProgress: true} : x
    )

    try {
      const acceptedUsers = [...set]

      await Backend.postJSON('/data/company-link-register-accept', {
        ...Backend.loadCred(),
        companyId: globals.getCompanyId(),
        acceptedUsers
      })
      this.state.pendingRegistrations = this.state.pendingRegistrations.filter(
        x => !set.has(x.userId)
      )
      this.state.pendingRegistrationsIds =
        this.state.pendingRegistrationsIds.filter(x => !set.has(x))

      const box = Sync.getCurrentSandbox()

      for (const {userId, projectIds, roleId} of invites) {
        for (const projectId of projectIds) {
          const project = box.getHub(projectId)
          if (!project) {
            logger.warn('Project not found', projectId)
            continue
          }

          UserAccess.setRoleInProject(userId, project, roleId)
        }

        box.connect(userId, box.companyId)
      }

      return true
    } catch (e) {
      MessageBox.show('Cannot accept user', 'warning')
      this.state.pendingRegistrations = this.state.pendingRegistrations.map(
        x => (set.has(x.userId) ? {...x, inProgress: false} : x)
      )
      return false
    }
  }

  async rejectSelectedRegistrations(): Promise<void> {
    const userIds = Array.from(this.state.selectedRegistrations)
    if (await this.rejectRegistrations(userIds)) {
      this.state.selectedRegistrations = new Set()

      if (!this.pendingRegistrationsSince.length) {
        this.setRegistrationPopupOpen(false)
      }
    }
  }

  async rejectRegistrations(userIds: DataObjectId[]): Promise<boolean> {
    if (
      !(await showConfirm(Lang.msg.users.confirmReject(userIds.length > 1)))
    ) {
      return false
    }

    const set = new Set(userIds)
    this.state.pendingRegistrations = this.state.pendingRegistrations.map(x =>
      set.has(x.userId) ? {...x, inProgress: true} : x
    )
    try {
      const companyId = globals.getCompanyId()
      const rejectedUsers = [...set] as DataObjectId[]
      await Backend.postJSON('/data/company-link-register-reject', {
        ...Backend.loadCred(),
        companyId,
        rejectedUsers
      })

      const box = Sync.getCurrentSandbox()

      for (const userId of rejectedUsers) {
        UserAccess.removeUserFromCompany(userId, box, true)
      }

      this.state.pendingRegistrations = this.state.pendingRegistrations.filter(
        x => !set.has(x.userId)
      )
      this.state.pendingRegistrationsIds =
        this.state.pendingRegistrationsIds.filter(x => !set.has(x))

      return true
    } catch (e) {
      MessageBox.show('Cannot reject user', 'warning')
      this.state.pendingRegistrations = this.state.pendingRegistrations.map(
        x => (set.has(x.userId) ? {...x, inProgress: false} : x)
      )
      return false
    }
  }

  resetState(): void {
    this.state = getInitialState()
  }

  dispose(): void {
    if (this.pendingRegistrationsCheckHandle) {
      clearInterval(this.pendingRegistrationsCheckHandle)
    }

    if (this.onCompanyChangeId !== undefined) {
      globals.removeCompanyListener(this.onCompanyChangeId)
    }
  }
}

export const pendingCompaniesAdminStore = new PendingCompaniesAdminStore()
